import React, { useRef, useEffect, useState } from 'react';
import { Button, Space, Row, Col, Typography, Alert, Tooltip, Spin, Radio } from 'antd';
import { DownloadOutlined, ZoomInOutlined, ZoomOutOutlined, UndoOutlined } from '@ant-design/icons';
import * as d3 from 'd3';
import './D3PhylogeneticTree.css';
import { parseNewick, parsePhyloXml, convertPhyloXmlToNewick, TreeNode } from '../../../utils/d3TreeUtils';

const { Text } = Typography;
const { Group: RadioGroup, Button: RadioButton } = Radio;

// 树的类型
type TreeType = 'horizontal' | 'vertical' | 'rectangular' | 'radial';

interface D3PhylogeneticTreeProps {
  newickString?: string;
  phyloXmlString?: string;
  width?: number;
  height?: number;
  showControls?: boolean;
}

const D3PhylogeneticTree: React.FC<D3PhylogeneticTreeProps> = ({
  newickString,
  phyloXmlString,
  width = 800,
  height = 600,
  showControls = true
}) => {
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const [treeType, setTreeType] = useState<TreeType>('horizontal');
  const [showBranchLengths, setShowBranchLengths] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  
  // 渲染树
  useEffect(() => {
    if (!treeContainerRef.current) return;
    
    try {
      setLoading(true);
      
      // 获取树数据
      let treeData: TreeNode | null = null;
      
      // 优先使用Newick字符串
      if (newickString) {
        try {
          treeData = parseNewick(newickString);
        } catch (e: any) {
          console.error('解析Newick字符串时出错:', e);
          setError(`解析Newick字符串时出错: ${e.message}`);
          // 不要直接返回，继续尝试其他数据源
        }
      } 
      
      // 如果Newick解析失败，尝试解析PhyloXML
      if (!treeData && phyloXmlString) {
        try {
          treeData = parsePhyloXml(phyloXmlString);
          if (!treeData) {
            setError('无法解析PhyloXML数据');
          }
        } catch (e: any) {
          console.error('解析PhyloXML时出错:', e);
          setError(`解析PhyloXML时出错: ${e.message}`);
        }
      }
      
      // 如果没有有效的树数据，使用示例数据
      if (!treeData) {
        try {
          const simpleNewick = "(A:0.1,B:0.2,(C:0.3,D:0.4):0.5);";
          treeData = parseNewick(simpleNewick);
          if (!error) {
            setError('使用示例数据');
          } else {
            setError(`${error} - 使用示例数据代替`);
          }
        } catch (e: any) {
          console.error('解析示例数据时出错:', e);
          setError(`无法加载任何树数据: ${e.message}`);
          setLoading(false);
          return;
        }
      }
      
      // 清除容器
      const container = treeContainerRef.current;
      container.innerHTML = '';
      
      // 调整容器大小以适应内容
      const containerWidth = width;
      const containerHeight = height;
      
      // 创建SVG元素
      const svg = d3.select(container)
        .append('svg')
        .attr('width', containerWidth)
        .attr('height', containerHeight)
        .attr('viewBox', [0, 0, containerWidth, containerHeight])
        .attr('style', 'max-width: 100%; height: auto;');
      
      // 添加缩放功能
      const zoom = d3.zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.1, 10])
        .on('zoom', (event) => {
          g.attr('transform', event.transform);
          setZoomLevel(event.transform.k);
        });
      
      svg.call(zoom);
      
      // 创建主要的图形元素组
      const g = svg.append('g');
      
      // 计算树的宽度
      const treeWidth = containerWidth - 40; // 减去一些边距
      
      // 根据树类型选择布局和位置
      let treeLayout: any;
      
      if (treeType === 'horizontal') {
        // 水平树布局 (生物学标准)
        g.attr('transform', `translate(${containerWidth * 0.05}, ${containerHeight / 2})`);
        treeLayout = d3.cluster<TreeNode>()
          .size([containerHeight - 100, treeWidth * 0.8])
          .separation((a, b) => (a.parent === b.parent ? 1 : 1.2));
      } else if (treeType === 'vertical') {
        // 垂直树布局
        const hMargin = containerWidth * 0.1;
        const vMarginTop = containerHeight * 0.1;
        const vMarginBottom = containerHeight * 0.1;
        g.attr('transform', `translate(${hMargin}, ${vMarginTop})`);
        treeLayout = d3.cluster<TreeNode>()
          .size([containerWidth - hMargin * 2, containerHeight - vMarginTop - vMarginBottom])
          .separation((a, b) => (a.parent === b.parent ? 1 : 1.2));
      } else if (treeType === 'rectangular') {
        // 直角树布局
        g.attr('transform', `translate(${containerWidth * 0.05}, ${containerHeight / 2})`);
        treeLayout = d3.tree<TreeNode>()
          .size([containerHeight - 100, treeWidth * 0.8])
          .separation((a, b) => (a.parent === b.parent ? 1 : 1.2));
      } else {
        // 径向树布局
        g.attr('transform', `translate(${treeWidth / 2}, ${containerHeight / 2})`);
        treeLayout = d3.cluster<TreeNode>()
          .size([2 * Math.PI, Math.min(treeWidth, containerHeight) / 2 - 100]);
      }
      
      // 创建层次结构
      const root = d3.hierarchy(treeData);
      
      // 应用布局
      treeLayout(root);
      
      // 绘制连接线
      if (treeType === 'horizontal') {
        // 水平布局 - 使用直角连接线 (生物学标准)
        g.selectAll('.link')
          .data(root.links())
          .join('path')
          .attr('class', 'link')
          .attr('d', d => `M${d.source.y || 0},${d.source.x || 0}V${d.target.x || 0}H${d.target.y || 0}`)
          .attr('fill', 'none')
          .attr('stroke', '#666')
          .attr('stroke-width', 1.5);
      } else if (treeType === 'vertical') {
        // 垂直布局 - 使用直角连接线
        g.selectAll('.link')
          .data(root.links())
          .join('path')
          .attr('class', 'link')
          .attr('d', d => `M${d.source.x || 0},${d.source.y || 0}H${d.target.x || 0}V${d.target.y || 0}`)
          .attr('fill', 'none')
          .attr('stroke', '#666')
          .attr('stroke-width', 1.5);
      } else if (treeType === 'rectangular') {
        // 直角树布局 - 使用曲线连接线
        g.selectAll('.link')
          .data(root.links())
          .join('path')
          .attr('class', 'link')
          .attr('d', d3.linkHorizontal<any, any>()
            .x(d => d.y || 0)
            .y(d => d.x || 0))
          .attr('fill', 'none')
          .attr('stroke', '#666')
          .attr('stroke-width', 1.5);
      } else {
        // 径向树布局 - 使用径向连接线
        g.selectAll('.link')
          .data(root.links())
          .join('path')
          .attr('class', 'link')
          .attr('d', d3.linkRadial<any, any>()
            .angle(d => d.x || 0)
            .radius(d => d.y || 0))
          .attr('fill', 'none')
          .attr('stroke', '#666')
          .attr('stroke-width', 1.5);
      }
      
      // 创建节点组
      const node = g.selectAll('.node')
        .data(root.descendants())
        .join('g')
        .attr('class', 'node')
        .attr('transform', d => {
          if (treeType === 'radial') {
            const y = d.y || 0;
            const x = d.x || 0;
            return `translate(${y * Math.sin(x)},${-y * Math.cos(x)})`;
          } else if (treeType === 'vertical') {
            return `translate(${d.x || 0},${d.y || 0})`;
          } else {
            // horizontal 和 rectangular 使用相同的坐标转换
            return `translate(${d.y || 0},${d.x || 0})`;
          }
        });
      
      // 添加节点圆圈
      node.append('circle')
        .attr('r', 3)
        .attr('fill', d => d.children ? '#fff' : '#4CAF50')
        .attr('stroke', '#4CAF50')
        .attr('stroke-width', 1.5);
      
      // 添加节点标签
      node.append('text')
        .attr('dy', '0.31em')
        .attr('x', d => {
          if (treeType === 'horizontal' || treeType === 'rectangular') {
            return d.children ? -8 : 8;
          } else if (treeType === 'vertical') {
            return 0;
          } else {
            // radial
            const x = d.x || 0;
            return x < Math.PI ? 8 : -8;
          }
        })
        .attr('y', d => {
          if (treeType === 'vertical') {
            return d.children ? -8 : 8;
          }
          return 0;
        })
        .attr('text-anchor', d => {
          if (treeType === 'horizontal' || treeType === 'rectangular') {
            return d.children ? 'end' : 'start';
          } else if (treeType === 'vertical') {
            return d.children ? 'middle' : 'end';
          } else {
            // radial
            const x = d.x || 0;
            return x < Math.PI ? 'start' : 'end';
          }
        })
        .attr('transform', d => {
          if (treeType === 'vertical' && !d.children) {
            return 'rotate(-45)';
          }
          return null; // 使用null代替"none"，这样D3会忽略transform属性
        })
        .attr('font-size', '10px')
        .text(d => d.data.name)
        .attr('fill', '#333')
        .clone(true).lower()
        .attr('stroke', 'white')
        .attr('stroke-width', 3);
      
      // 如果显示分支长度，添加分支长度标签
      if (showBranchLengths) {
        if (treeType === 'horizontal') {
          // 水平布局的分支长度标签
          g.selectAll('.branch-length')
            .data(root.links())
            .join('text')
            .attr('class', 'branch-length')
            .attr('x', d => {
              const sourceY = d.source.y || 0;
              const targetY = d.target.y || 0;
              const sourceX = d.source.x || 0;
              const targetX = d.target.x || 0;
              
              if (sourceX === targetX) {
                // 水平分支（水平线）
                return (sourceY + targetY) / 2; // 在中间放置
              } else {
                // 垂直分支（垂直线）
                return targetY - 15; // 在线左侧放置
              }
            })
            .attr('y', d => {
              const sourceX = d.source.x || 0;
              const targetX = d.target.x || 0;
              
              if (sourceX === targetX) {
                // 水平分支（水平线）
                return sourceX - 8; // 在线上方放置
              } else {
                // 垂直分支（垂直线）
                return (sourceX + targetX) / 2; // 在中间放置
              }
            })
            .attr('text-anchor', d => {
              const sourceX = d.source.x || 0;
              const targetX = d.target.x || 0;
              
              if (sourceX === targetX) {
                // 水平分支（水平线）
                return 'middle'; // 居中对齐
              } else {
                // 垂直分支（垂直线）
                return 'end'; // 右对齐
              }
            })
            .attr('font-size', '9px')
            .attr('fill', '#666')
            .attr('stroke', 'white')  // 添加白色描边，增强可见度
            .attr('stroke-width', '0.5')
            .attr('paint-order', 'stroke')  // 先绘制描边，再绘制文字
            .text(d => (d.target.data as TreeNode).length?.toFixed(3) || '');
        } else if (treeType === 'vertical') {
          // 垂直布局的分支长度标签
          g.selectAll('.branch-length')
            .data(root.links())
            .join('text')
            .attr('class', 'branch-length')
            .attr('x', d => {
              // 对于垂直树，水平分支上的标签应该居中
              const sourceX = d.source.x || 0;
              const targetX = d.target.x || 0;
              
              if (sourceX === targetX) {
                // 垂直分支（垂直线）
                return sourceX + 10; // 在线右侧放置
              } else {
                // 水平分支（水平线）
                return (sourceX + targetX) / 2; // 在中间放置
              }
            })
            .attr('y', d => {
              const sourceY = d.source.y || 0;
              const targetY = d.target.y || 0;
              const sourceX = d.source.x || 0;
              const targetX = d.target.x || 0;
              
              if (sourceX === targetX) {
                // 垂直分支（垂直线）
                return (sourceY + targetY) / 2; // 在中间放置
              } else {
                // 水平分支（水平线）
                return targetY - 8; // 在线上方放置
              }
            })
            .attr('text-anchor', d => {
              const sourceX = d.source.x || 0;
              const targetX = d.target.x || 0;
              
              if (sourceX === targetX) {
                // 垂直分支（垂直线）
                return 'start'; // 左对齐
              } else {
                // 水平分支（水平线）
                return 'middle'; // 居中对齐
              }
            })
            .attr('font-size', '9px')
            .attr('fill', '#666')
            .attr('stroke', 'white')  // 添加白色描边，增强可见度
            .attr('stroke-width', '0.5')
            .attr('paint-order', 'stroke')  // 先绘制描边，再绘制文字
            .text(d => (d.target.data as TreeNode).length?.toFixed(3) || '');
        } else if (treeType === 'rectangular') {
          // 直角树布局的分支长度标签
          g.selectAll('.branch-length')
            .data(root.links())
            .join('text')
            .attr('class', 'branch-length')
            .attr('x', d => ((d.source.y || 0) + (d.target.y || 0)) / 2)
            .attr('y', d => ((d.source.x || 0) + (d.target.x || 0)) / 2 - 5)
            .attr('text-anchor', 'middle')
            .attr('font-size', '9px')
            .attr('fill', '#666')
            .attr('stroke', 'white')  // 添加白色描边，增强可见度
            .attr('stroke-width', '0.5')
            .attr('paint-order', 'stroke')  // 先绘制描边，再绘制文字
            .text(d => (d.target.data as TreeNode).length?.toFixed(3) || '');
        } else {
          // 径向树布局的分支长度标签
          g.selectAll('.branch-length')
            .data(root.links())
            .join('text')
            .attr('class', 'branch-length')
            .attr('x', d => {
              const midX = ((d.source.y || 0) + (d.target.y || 0)) / 2;
              const midAngle = ((d.source.x || 0) + (d.target.x || 0)) / 2;
              return midX * Math.sin(midAngle);
            })
            .attr('y', d => {
              const midY = ((d.source.y || 0) + (d.target.y || 0)) / 2;
              const midAngle = ((d.source.x || 0) + (d.target.x || 0)) / 2;
              return -midY * Math.cos(midAngle);
            })
            .attr('dy', -5)
            .attr('text-anchor', 'middle')
            .attr('font-size', '9px')
            .attr('fill', '#666')
            .attr('stroke', 'white')  // 添加白色描边，增强可见度
            .attr('stroke-width', '0.5')
            .attr('paint-order', 'stroke')  // 先绘制描边，再绘制文字
            .text(d => (d.target.data as TreeNode).length?.toFixed(3) || '');
        }
      }
      
      setLoading(false);
    } catch (e: any) {
      console.error('渲染树时出错:', e);
      setError(`渲染进化树时出错: ${e.message}`);
      setLoading(false);
      
      // 显示错误信息
      if (treeContainerRef.current) {
        treeContainerRef.current.innerHTML = `<div style="color: red; text-align: center;">渲染进化树时出错: ${e.message}</div>`;
      }
    }
  }, [newickString, phyloXmlString, width, height, treeType, showBranchLengths]);
  
  // 下载SVG图片
  const handleDownloadSVG = () => {
    if (!treeContainerRef.current) return;
    
    try {
      // 获取SVG元素
      const svgElement = treeContainerRef.current.querySelector('svg');
      if (!svgElement) return;
      
      // 创建SVG数据
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], {type: 'image/svg+xml'});
      const svgUrl = URL.createObjectURL(svgBlob);
      
      // 执行下载
      const link = document.createElement('a');
      link.href = svgUrl;
      link.download = '进化树.svg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // 释放URL对象
      setTimeout(() => URL.revokeObjectURL(svgUrl), 100);
    } catch (e: any) {
      console.error('下载SVG时出错:', e);
      setError('下载SVG时出错');
    }
  };
  
  // 放大缩小功能
  const handleZoom = (factor: number) => {
    if (!treeContainerRef.current) return;
    
    try {
      const svg = d3.select(treeContainerRef.current).select('svg');
      const currentZoom = d3.zoomTransform(svg.node() as any);
      
      svg.call(
        d3.zoom<SVGSVGElement, unknown>().transform as any,
        d3.zoomIdentity
          .translate(currentZoom.x, currentZoom.y)
          .scale(currentZoom.k * factor)
      );
      
      setZoomLevel(prev => prev * factor);
    } catch (e: any) {
      console.error('缩放时出错:', e);
    }
  };
  
  // 重置视图
  const handleReset = () => {
    if (!treeContainerRef.current) return;
    
    try {
      const svg = d3.select(treeContainerRef.current).select('svg');
      
      svg.call(
        d3.zoom<SVGSVGElement, unknown>().transform as any,
        d3.zoomIdentity
      );
      
      setZoomLevel(1);
    } catch (e: any) {
      console.error('重置视图时出错:', e);
    }
  };

  return (
    <div className="d3-phylogenetic-tree-viewer">
      {showControls && (
        <div className="tree-controls">
          <Row gutter={[16, 16]} align="middle">
            <Col span={24}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <span style={{ marginRight: '10px', fontWeight: 'bold' }}>树形式: </span>
                  <RadioGroup value={treeType} onChange={e => setTreeType(e.target.value)}>
                    <RadioButton value="horizontal">水平树</RadioButton>
                    <RadioButton value="vertical">垂直树</RadioButton>
                    <RadioButton value="rectangular">曲线树</RadioButton>
                    <RadioButton value="radial">辐射树</RadioButton>
                  </RadioGroup>
                </div>
                
                <div className="zoom-controls">
                  <Button 
                    onClick={() => setShowBranchLengths(!showBranchLengths)} 
                    type={showBranchLengths ? 'primary' : 'default'}
                    style={{ marginRight: '10px' }}
                  >
                    {showBranchLengths ? '隐藏分支长度' : '显示分支长度'}
                  </Button>
                  
                  <Space>
                    <Button icon={<ZoomInOutlined />} onClick={() => handleZoom(1.2)}>
                      放大
                    </Button>
                    <Button icon={<ZoomOutOutlined />} onClick={() => handleZoom(0.8)}>
                      缩小
                    </Button>
                    <Button icon={<UndoOutlined />} onClick={handleReset}>
                      重置
                    </Button>
                    <Button icon={<DownloadOutlined />} onClick={handleDownloadSVG}>
                      下载SVG
                    </Button>
                  </Space>
                  
                  {zoomLevel !== 1 && (
                    <Text type="secondary" style={{ marginLeft: '10px' }}>
                      缩放: {zoomLevel.toFixed(1)}x
                    </Text>
                  )}
                </div>
              </Space>
            </Col>
          </Row>
        </div>
      )}
      
      {error && (
        <Alert 
          message="警告" 
          description={error} 
          type="warning" 
          showIcon 
          style={{ marginBottom: '16px' }}
        />
      )}
      
      <div 
        ref={treeContainerRef} 
        className="tree-container"
        style={{ 
          width: width, 
          height: height,
          border: '1px solid #f0f0f0',
          borderRadius: '4px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {loading && (
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.8)'
          }}>
            <Spin tip="加载中..." />
          </div>
        )}
      </div>
    </div>
  );
};

export default D3PhylogeneticTree; 